import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg, today } from "@/lib/api";
import { Consent } from "@/components/site/Bits";

type Extra = "date" | "party" | "pickup" | "dropoff" | "flight" | "market" | "nights" | "month";

export const InquiryForm = ({ kind, extras = [], subjectId, subjectTitle, cta = "Send inquiry", testid = "inquiry-form", successTitle = "Inquiry received" }:
  { kind: string; extras?: Extra[]; subjectId?: string; subjectTitle?: string; cta?: string; testid?: string; successTitle?: string }) => {
  const [f, setF] = useState<Record<string, string>>({ name: "", email: "", phone: "", message: "", preferred_date: "", party_size: "", pickup: "", dropoff: "", flight_number: "", market: extras.includes("market") ? "Accra" : "", planned_nights: "", travel_month: "" });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState("");
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agree) return toast.error("Please accept the privacy policy");
    setBusy(true);
    try {
      const body: any = { kind, ...f, subject_id: subjectId, subject_title: subjectTitle, accept_privacy: agree, party_size: f.party_size ? Number(f.party_size) : null, planned_nights: f.planned_nights ? Number(f.planned_nights) : null };
      Object.keys(body).forEach((k) => body[k] === "" && delete body[k]);
      const r = await api.post("/inquiries", body);
      setRef(r.data.reference);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (ref)
    return (
      <div ref={(el) => el?.scrollIntoView({ behavior: "smooth", block: "center" })} className="card-lux space-y-3 p-6" data-testid={`${testid}-success`}>
        <CheckCircle2 className="text-emerald-600" />
        <h3 className="text-xl">{successTitle}</h3>
        <p className="text-sm text-navy/65">Reference <span className="font-mono font-bold" data-testid={`${testid}-reference`}>{ref}</span>. Our team will reply by email. Nothing has been booked or charged.</p>
      </div>
    );

  return (
    <form onSubmit={submit} className="card-lux space-y-3 p-6 sm:p-7" data-testid={testid}>
      <div className="grid gap-3 sm:grid-cols-2">
        <input required placeholder="Full name *" value={f.name} onChange={set("name")} className="field" data-testid={`${testid}-name`} />
        <input required type="email" placeholder="Email *" value={f.email} onChange={set("email")} className="field" data-testid={`${testid}-email`} />
        <input placeholder="Phone / WhatsApp" value={f.phone} onChange={set("phone")} className="field" data-testid={`${testid}-phone`} />
        {extras.includes("date") && <input type="date" min={today()} value={f.preferred_date} onChange={set("preferred_date")} className="field" data-testid={`${testid}-date`} />}
        {extras.includes("party") && <input type="number" min={1} placeholder="Number of people" value={f.party_size} onChange={set("party_size")} className="field" data-testid={`${testid}-party`} />}
        {extras.includes("flight") && <input placeholder="Flight number" value={f.flight_number} onChange={set("flight_number")} className="field" data-testid={`${testid}-flight`} />}
        {extras.includes("pickup") && <input placeholder="Pick-up location" value={f.pickup} onChange={set("pickup")} className="field" data-testid={`${testid}-pickup`} />}
        {extras.includes("dropoff") && <input placeholder="Drop-off location" value={f.dropoff} onChange={set("dropoff")} className="field" data-testid={`${testid}-dropoff`} />}
        {extras.includes("market") && (
          <select value={f.market} onChange={set("market")} className="field" data-testid={`${testid}-market`}>
            <option value="Accra">Accra, Ghana</option><option value="United States">United States (Texas)</option>
          </select>
        )}
        {extras.includes("nights") && <input type="number" min={1} placeholder="Planned nights" value={f.planned_nights} onChange={set("planned_nights")} className="field" data-testid={`${testid}-nights`} />}
        {extras.includes("month") && <label className="block"><span className="label">Expected travel month</span><input type="month" min="2027-01" value={f.travel_month} onChange={set("travel_month")} className="field" data-testid={`${testid}-month`} /></label>}
      </div>
      <textarea rows={4} placeholder="How can we help?" value={f.message} onChange={set("message")} className="field" data-testid={`${testid}-message`} />
      <Consent testid={`${testid}-consent`} checked={agree} onChange={setAgree} text={<>I agree to the <Link to="/policies/privacy" className="underline">Privacy Policy</Link> and to be contacted about this inquiry.</>} />
      <button disabled={busy} className="btn-navy w-full sm:w-auto" data-testid={`${testid}-submit`}>{busy && <Loader2 size={16} className="animate-spin" />} {cta}</button>
    </form>
  );
};
